/* eslint-disable react/jsx-indent */
/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { Grid, makeStyles, Tab, Tabs, Tooltip } from '@material-ui/core';
import ReportProblemIcon from '@material-ui/icons/ReportProblem';
import Loading from 'components/Loading';
import i18n from 'i18n';
import {
  editEnvironmentsRequest,
  editSecurityRequest,
  getAllModelsRequest,
} from 'modules/environments/actions';
import {
  EnvironmentFields,
  ListModelFieldsDataProps,
} from 'modules/environments/interfaces';
import React from 'react';
import { connect } from 'react-redux';
import { useParams } from 'react-router';
import ApplicationState from 'store/interfaces';
import clsx from 'clsx';
import {
  CompanyData,
  PartnerData,
  PermissionsData,
  RolesEnum,
} from 'modules/authenticate/interfaces';
import {
  inAdminView,
  inOperatorView,
  inSuperAdminView,
  isClientOperator,
} from 'utils/role';
import FormEnvironment from '../Form';
import EnvironmentsTemplate from '../Templates/Environments';
import FormBehavior from './FormBehavior';
import FormSecurity from './FormSecurity';
import styles from './styles';
import ListApplicationsEnvironment from './ListApplicationsEnvironment';
import Monitoring from './Monitoring';
import MigrateEnvironment from './MigrateEnv';
import BulkClients from './BulkClients';
import LicenseGroups from './LicenseGroups';
import EnvironmentAdvisors from './Advisors';
import ListPlatformApplicationsEnvironment from './ListPlatformApplicationsEnvironment';

const useStyles = makeStyles(styles);

interface Props {
  environmentSelected: EnvironmentFields | null;
  modelsList: ListModelFieldsDataProps;
  isLoading: boolean;
  role?: RolesEnum;
  companySelected: CompanyData | null;
  partnerSelected: PartnerData | null;
  permissions: PermissionsData | undefined;
  editEnvironmentsRequest: ({ id, values }) => void;
  editSecurityRequest: ({
    securityValues,
    zeroTrustValues,
    deviceValidationValues,
    id,
  }) => void;
  getAllModelsRequest: ({ id }) => void;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: any;
  value: any;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  const classes = useStyles();

  return (
    <div
      className={classes.tabPanelContent}
      hidden={value !== index}
      {...other}
    >
      {value === index && children}
    </div>
  );
}

const Configuration = ({
  environmentSelected,
  modelsList,
  isLoading,
  role,
  companySelected,
  permissions,
  editSecurityRequest,
  getAllModelsRequest,
  editEnvironmentsRequest,
}: Props) => {
  const classes = useStyles();
  const { id, section } = useParams<{ id: string; section: string }>();
  const [currentTab, setCurrentTab] = React.useState(
    section === 'licenseGroup' ? 7 : 0
  );
  const [loading, setLoading] = React.useState(isLoading);

  function handleEditSecurity({
    securityValues,
    zeroTrustValues,
    deviceValidationValues,
  }) {
    editSecurityRequest({
      securityValues,
      zeroTrustValues,
      deviceValidationValues,
      id,
    });
  }

  function handleSubmitEdit(values) {
    editEnvironmentsRequest({ id, values });
  }

  React.useEffect(() => {
    getAllModelsRequest({ id });
  }, [getAllModelsRequest, id]);

  React.useEffect(() => {
    setLoading(isLoading);
  }, [isLoading]);

  const isMultiCloud = () => {
    return environmentSelected?.provider?.type === 'multicloud';
  };

  const configTabs = [
    {
      component: (
        <FormEnvironment
          isEdit
          initialValues={environmentSelected}
          environmentModel={modelsList.results}
          onSubmit={handleSubmitEdit}
          isLoading={loading}
        />
      ),
      hidden: isClientOperator(role),
    },
    {
      component: (
        <FormBehavior
          modeName={environmentSelected?.behavior?.type!}
          environmentTimeZone={environmentSelected?.behavior?.timezone!}
          environmentComments={environmentSelected?.behavior?.comments!}
          environmentId={id}
          isLoading={loading}
        />
      ),
      hidden: isMultiCloud() || !permissions?.edit_behavior,
    },
    {
      component: (
        <FormSecurity
          onSubmit={({
            securityValues,
            zeroTrustValues,
            deviceValidationValues,
          }) =>
            handleEditSecurity({
              securityValues,
              zeroTrustValues,
              deviceValidationValues,
            })
          }
        />
      ),
      hidden: isClientOperator(role),
    },
    {
      component: <Monitoring environmentName={environmentSelected?.name} />,
      hidden:
        !permissions?.can_edit_environment_monitoring_settings ||
        isClientOperator(role),
    },
    {
      component:
        environmentSelected?.client_apps?.registration_source === 'platform' ? (
          <ListPlatformApplicationsEnvironment />
        ) : (
          <ListApplicationsEnvironment />
        ),
      hidden: !permissions?.list_applications_environment,
    },
    {
      component: <BulkClients />,
      hidden: !permissions?.can_edit_clients_in_bulk || isClientOperator(role),
    },
    {
      component: (
        <MigrateEnvironment environmentSelected={environmentSelected} />
      ),
      hidden: !permissions?.migrate_environment || isClientOperator(role),
    },
    {
      component: <LicenseGroups />,
      hidden:
        !permissions?.list_license_groups ||
        !environmentSelected?.licenses.control_licenses_by_client,
    },
    {
      component: <EnvironmentAdvisors />,
      hidden: !permissions?.list_scaling_advisors,
    },
  ].filter(item => !item.hidden);

  return (
    <EnvironmentsTemplate
      currentPage="configuration"
      titlePage={environmentSelected?.name}
      breadcrumbs={[
        {
          name: i18n.t('ENVIRONMENTS_KEY'),
          link: '/environments',
        },
        {
          name: i18n.t('SETTINGS_KEY'),
        },
      ]}
      titleContent={i18n.t('ENVIRONMENTS_PAGE.CONFIGURATION_TITLE_KEY')}
      subtitleContent={`${environmentSelected?.name ||
        i18n.t('LOADING_KEY')} ${i18n.t(
        'ENVIRONMENTS_PAGE.CONFIGURATION_SUBTITLE_KEY'
      )}`}
    >
      <Grid container wrap="nowrap" className={classes.rootTabs}>
        <Tabs
          value={currentTab}
          onChange={(e, newValue) => setCurrentTab(newValue)}
          variant="scrollable"
          classes={{
            flexContainer: classes.tabsFlexContainer,
          }}
          scrollButtons="on"
          className={clsx({
            [classes.tabsGreen]: inAdminView(role, companySelected),
            [classes.tabsGray]: inSuperAdminView(role, companySelected),
            [classes.tabsBlue]: inOperatorView(role, companySelected),
            [classes.tabsYellow]: companySelected,
          })}
        >
          {!isClientOperator(role) && (
            <Tab
              icon={
                environmentSelected?.in_maintenance ? (
                  <Tooltip
                    placement="top"
                    arrow
                    title={i18n.t('MAINTENANCE_WARNING_KEY')}
                  >
                    <ReportProblemIcon
                      className={classes.iconReport}
                      color="primary"
                    />
                  </Tooltip>
                ) : (
                  ''
                )
              }
              id="btn-general"
              label={i18n.t('GENERAL_KEY')}
              className={clsx({
                [classes.tabGreen]: inAdminView(role, companySelected),
                [classes.tabGray]: inSuperAdminView(role, companySelected),
                [classes.tabBlue]: inOperatorView(role, companySelected),
                [classes.tabYellow]: companySelected,
              })}
            />
          )}
          {!isMultiCloud() && permissions?.edit_behavior && (
            <Tab
              id="btn-behavior"
              label={i18n.t('BEHAVIOR_KEY')}
              className={clsx({
                [classes.tabGreen]: inAdminView(role, companySelected),
                [classes.tabGray]: inSuperAdminView(role, companySelected),
                [classes.tabBlue]: inOperatorView(role, companySelected),
                [classes.tabYellow]: companySelected,
              })}
              hidden={environmentSelected?.provider?.type === 'multicloud'}
            />
          )}
          {!isClientOperator(role) && (
            <Tab
              id="btn-security"
              label={i18n.t('SECURITY_KEY')}
              className={clsx({
                [classes.tabGreen]: inAdminView(role, companySelected),
                [classes.tabGray]: inSuperAdminView(role, companySelected),
                [classes.tabBlue]: inOperatorView(role, companySelected),
                [classes.tabYellow]: companySelected,
              })}
            />
          )}
          {permissions?.can_edit_environment_monitoring_settings &&
            !isClientOperator(role) && (
              <Tab
                id="btn-monitoring"
                label={i18n.t('MONITORING_KEY')}
                className={clsx({
                  [classes.tabGreen]: inAdminView(role, companySelected),
                  [classes.tabGray]: inSuperAdminView(role, companySelected),
                  [classes.tabBlue]: inOperatorView(role, companySelected),
                  [classes.tabYellow]: companySelected,
                })}
              />
            )}
          {permissions?.list_applications_environment && (
            <Tab
              id="btn-applications"
              label={i18n.t('APPLICATIONS_KEY')}
              className={clsx({
                [classes.tabGreen]: inAdminView(role, companySelected),
                [classes.tabGray]: inSuperAdminView(role, companySelected),
                [classes.tabBlue]: inOperatorView(role, companySelected),
                [classes.tabYellow]: companySelected,
              })}
            />
          )}
          {permissions?.can_edit_clients_in_bulk && !isClientOperator(role) && (
            <Tab
              id="btn-clients"
              label={i18n.t('BULK_CLIENTS.CLIENTS_KEY')}
              className={clsx({
                [classes.tabGreen]: inAdminView(role, companySelected),
                [classes.tabGray]: inSuperAdminView(role, companySelected),
                [classes.tabBlue]: inOperatorView(role, companySelected),
                [classes.tabYellow]: companySelected,
              })}
            />
          )}
          {permissions?.migrate_environment && !isClientOperator(role) && (
            <Tab
              id="btn-migrate"
              label={i18n.t('MIGRATE_ENVIRONMENT_KEY')}
              className={clsx({
                [classes.tabGreen]: inAdminView(role, companySelected),
                [classes.tabGray]: inSuperAdminView(role, companySelected),
                [classes.tabBlue]: inOperatorView(role, companySelected),
                [classes.tabYellow]: companySelected,
              })}
            />
          )}
          {permissions?.list_license_groups &&
            environmentSelected?.licenses.control_licenses_by_client && (
              <Tab
                id="btn-license-group"
                label={i18n.t('LICENSE_GROUP_KEY')}
                className={clsx({
                  [classes.tabGreen]: inAdminView(role, companySelected),
                  [classes.tabGray]: inSuperAdminView(role, companySelected),
                  [classes.tabBlue]: inOperatorView(role, companySelected),
                  [classes.tabYellow]: companySelected,
                })}
              />
            )}
          {permissions?.list_scaling_advisors && (
            <Tab
              id="btn-advisors"
              label={i18n.t('ADVISORS_KEY')}
              className={clsx({
                [classes.tabGreen]: inAdminView(role, companySelected),
                [classes.tabGray]: inSuperAdminView(role, companySelected),
                [classes.tabBlue]: inOperatorView(role, companySelected),
                [classes.tabYellow]: companySelected,
              })}
            />
          )}
        </Tabs>
      </Grid>

      {environmentSelected ? (
        <Grid container>
          {configTabs.map((item, index) => (
            <TabPanel key={index} value={currentTab} index={index}>
              {item?.component}
            </TabPanel>
          ))}
        </Grid>
      ) : (
        <Loading />
      )}
    </EnvironmentsTemplate>
  );
};

const mapStateToProps = ({
  environments: { environmentSelected, modelsList, isLoading },
  authenticate: { partnerSelected, companySelected, data },
}: ApplicationState) => ({
  environmentSelected,
  modelsList,
  isLoading,
  partnerSelected,
  companySelected,
  role: data?.role,
  permissions: data?.permissions,
});

export default connect(mapStateToProps, {
  editSecurityRequest,
  getAllModelsRequest,
  editEnvironmentsRequest,
})(Configuration);
